import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { DimensionValue } from 'react-native'
import styled from 'styled-components/native'
import { AvailabilitySlot } from '@dialogue/timekeeper'

import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { isWeb } from 'APP/Helpers/checkPlatform'
import I18n from 'APP/I18n'
import {
  selectCreateAppointmentRequest,
  selectLoadingProviderBio,
  selectProvider,
  selectProviderById,
} from 'APP/Store/ProviderBooking/selectors'
import { providerBookingActions } from 'APP/Store/ProviderBooking'
import Button from 'APP/Converse/Button'
import BottomSheetModal from 'APP/Components/BottomSheetModal'
import { LoadingIndicator } from 'APP/Components/AppointmentBooking/common/loadingIndicator'
import { InsuranceReimbursementNotice } from 'APP/Components/InsuranceReimbursementNotice'
import { ProfileHeader } from './ProfileHeader'
import { ProviderAvailabilities, ProviderAvailability } from './ProviderAvailabilities'
import { isOCAppointment } from 'APP/Lib/AppointmentManagementHelpers'
import Analytics from 'APP/Services/Analytics'

const Wrapper = styled.View`
  flex: 1;
  padding-bottom: ${({ theme }) => theme.insets.bottom}px;
`

const ContentScrollingView = styled.ScrollView.attrs({
  contentContainerStyle: {
    flexGrow: 1,
  },
})``

const ContentView = styled.View`
  padding: 0 ${({ theme }) => theme.metrics.baseMargin}px;
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
`

const ConfirmationWrapper = styled.View`
  background-color: ${({ theme }) => theme.colors.background};
  box-shadow: 0px -8px 8px ${({ theme }) => theme.colors.background};
  elevation: 4;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
  gap: ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

export interface Selection {
  choice: string
  providerId: number
}

interface Props {
  selectedProviderId?: number | null
  baseChosenTimeslot?: AvailabilitySlot | null
  selection?: Selection | null
  appointmentType?: string
  withAvailabilitySelection?: boolean
  isLoadingSlots?: boolean
  confirmDisabled?: boolean
  onConfirm: (slot: AvailabilitySlot | null) => boolean
  onClose: () => void
}

export const ProviderProfileModal = ({
  selectedProviderId,
  baseChosenTimeslot,
  withAvailabilitySelection = false,
  isLoadingSlots = false,
  confirmDisabled = false,
  selection,
  appointmentType,
  onConfirm,
  onClose,
}: Props) => {
  const dispatch = useDispatch()
  const [selectedTimeslot, setSelectedTimeslot] = useState<AvailabilitySlot | null>(
    baseChosenTimeslot || null
  )
  const providerId = baseChosenTimeslot?.provider_id || selection?.providerId || selectedProviderId
  // @ts-expect-error FIXME: providerId may be falsy, either require it or select the provider conditionally here
  const provider = useSelector((state) => selectProvider(state, providerId))
  const isBioLoading = useSelector(selectLoadingProviderBio)
  const bioWasPresentOnOpenRef = useRef(false)
  const providerData = useSelector(selectProviderById)
  const {
    loading: isCreatingAppointment,
    data: createdAppointment,
    error: appointmentCreationError,
  } = useSelector(selectCreateAppointmentRequest)

  useEffect(() => {
    if (providerId) {
      bioWasPresentOnOpenRef.current = provider?.bio !== undefined
      dispatch(providerBookingActions.getProviderById(providerId))
      if (provider?.bio === undefined) {
        dispatch(providerBookingActions.getProviderBio(providerId))
      }
    }
  }, [providerId, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps

  const showLoader = !bioWasPresentOnOpenRef.current && (isBioLoading || isLoadingSlots)

  // When an appointment has been booked, close this modal.
  useEffect(() => {
    if (createdAppointment) {
      onClose()
    }
  }, [createdAppointment, onClose])

  // Reset the selected timeslot if the appointment creation fails.
  useEffect(() => {
    if (appointmentCreationError) {
      setSelectedTimeslot(null)
    }
  }, [appointmentCreationError])

  // If parent component has an opinion of what timeslot should be selected, let's honour it.
  useEffect(() => {
    if (baseChosenTimeslot) {
      setSelectedTimeslot(baseChosenTimeslot)
    }
  }, [baseChosenTimeslot])

  const handleCancel = useCallback(() => {
    setSelectedTimeslot(null)
    onClose()
  }, [onClose])

  const handleConfirm = useCallback(() => {
    const closeOnConfirm = onConfirm(selectedTimeslot)

    if (closeOnConfirm) onClose()
  }, [onConfirm, onClose, selectedTimeslot])

  const handleBookSomeoneElse = useCallback(() => {
    Analytics.trackEvent('button_click', {
      button_value: 'providerBooking.bookSomeoneElse',
    })
    handleCancel()
  }, [handleCancel])

  const hasSelection = !!(selectedTimeslot || selection)

  return (
    <BottomSheetModal
      isVisible={!!providerId}
      onClose={handleCancel}
      style={{
        height: '100%',
      }}
      containerStyle={{
        height: '95%',
        ...(isWeb()
          ? // istanbul ignore next
            { width: '100%', maxWidth: '1000px' as DimensionValue, alignSelf: 'center' }
          : {}),
      }}
      title={I18n.t('AppointmentBooking.profile_title', { name: provider?.givenName })}
      testID="PROVIDER_PROFILE_MODAL"
    >
      {provider && (
        <Wrapper>
          {showLoader ? (
            <LoadingIndicator />
          ) : (
            <>
              <ContentScrollingView>
                <ContentView onStartShouldSetResponder={() => true}>
                  <ProfileHeader provider={provider} providerData={providerData} />
                  {isOCAppointment(appointmentType) && <InsuranceReimbursementNotice />}
                  {withAvailabilitySelection ? (
                    <ProviderAvailabilities
                      providerId={provider.id}
                      selectedSlot={selectedTimeslot}
                      onSelect={setSelectedTimeslot}
                    />
                  ) : (
                    hasSelection && (
                      <ProviderAvailability slot={selectedTimeslot} selection={selection?.choice} />
                    )
                  )}
                </ContentView>
              </ContentScrollingView>
              {(hasSelection || withAvailabilitySelection) && (
                <ConfirmationWrapper>
                  {hasSelection && (
                    <Button
                      variant="primary"
                      widthVariant="full"
                      title={I18n.t('AppointmentBooking.confirm_appointment')}
                      testID="APPOINTMENT_BOOKING_CONFIRM_BTN"
                      onPress={handleConfirm}
                      disabled={isCreatingAppointment || confirmDisabled}
                    />
                  )}
                  {withAvailabilitySelection && (
                    <Button
                      variant="tertiary"
                      title={I18n.t('AppointmentBooking.book_someone_else')}
                      testID="APPOINTMENT_BOOKING_BOOK_SOMEONE_ELSE_BTN"
                      onPress={handleBookSomeoneElse}
                      widthVariant="full"
                    />
                  )}
                </ConfirmationWrapper>
              )}
            </>
          )}
        </Wrapper>
      )}
    </BottomSheetModal>
  )
}
